import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { A } from '@adornis/base/env-info.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { map, of } from 'rxjs';
import { AdminInstitutionViewSetting } from '../entities/AdminInstitutionViewSetting.js';
import { Permission } from '../enums/roles.js';

export const getViewSettings = registerQuery({
  operationName: 'getViewSettings',
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  type: () => AdminInstitutionViewSetting,
  middlewares: [
    checkAuthorizedMiddleware(
      ({ user, params }) =>
        user._id === params.userID && user.hasPermission(Permission.VIEW_ADMIN_DASHBOARD, params.institutionID),
    ),
  ],
  resolve:
    (userID: string, institutionID: string) =>
    async <SelSet extends BaseQLSelectionSet<AdminInstitutionViewSetting>>(
      gelFields: SelSet,
    ): Promise<NarrowedEntity<AdminInstitutionViewSetting, SelSet>> => {
      const col = await scopedCollection(AdminInstitutionViewSetting._class);
      const viewSettings = await col.findOne({ userID, institutionID });
      if (viewSettings) return viewSettings as any;

      // create if not exist
      const newViewSettings = new AdminInstitutionViewSetting({ _id: A.getGloballyUniqueID(), userID, institutionID });
      await newViewSettings.create();
      return newViewSettings as any;
    },
});

export const subscribeAdminInstitutionViewSetting = registerSubscription({
  operationName: 'subscribeAdminInstitutionViewSetting',
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  type: () => AdminInstitutionViewSetting,
  middlewares: [
    checkAuthorizedMiddleware(
      ({ user, params }) =>
        user._id === params.userID && user.hasPermission(Permission.VIEW_ADMIN_DASHBOARD, params.institutionID),
    ),
  ],
  resolve: (userID: string, institutionID: string) => {
    return (qglFields: BaseQLSelectionSet<AdminInstitutionViewSetting>) => {
      return watchQuery(AdminInstitutionViewSetting._collectionName, { userID, institutionID }).pipe(
        map(viewSettings => viewSettings.at(0) as AdminInstitutionViewSetting),
      );
    };
  },
});

export const getAdminInstitutionViewSettingsByIDs = registerQuery({
  operationName: 'getAdminInstitutionViewSettingsByIDs',
  type: () => [AdminInstitutionViewSetting],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdminInstitutionViewSetting>>(gqlFields: SelSet) =>
      readEntitiesByIDs(AdminInstitutionViewSetting, ids, gqlFields),
});

export const subscribeAdminInstitutionViewSettingsByIDs = registerSubscription({
  operationName: 'subscribeAdminInstitutionViewSettingsByIDs',
  type: () => [AdminInstitutionViewSetting],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdminInstitutionViewSetting>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(AdminInstitutionViewSetting, ids, gqlFields),
});

export const getAllAdminInstitutionViewSettings = registerQuery({
  operationName: 'getAllAdminInstitutionViewSettings',
  type: () => [AdminInstitutionViewSetting],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdminInstitutionViewSetting>>(gqlFields: SelSet) =>
      readAllEntities(AdminInstitutionViewSetting, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllAdminInstitutionViewSettings = registerSubscription({
  operationName: 'subscribeAllAdminInstitutionViewSettings',
  type: () => [AdminInstitutionViewSetting],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdminInstitutionViewSetting>>(gqlFields: SelSet) =>
      watchAllEntities(AdminInstitutionViewSetting, gqlFields, { skip, limit, excludeIDs }),
});

export const getAdminInstitutionViewSettingByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<AdminInstitutionViewSetting>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getAdminInstitutionViewSettingsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeAdminInstitutionViewSettingByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<AdminInstitutionViewSetting>>(gqlFields: SelSet) =>
    id
      ? subscribeAdminInstitutionViewSettingsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null))
      : of(null);
